/**
 * Question page shell for VariantB.
 *
 * Layout contract (all steps share this):
 *   viewport → logo → stepper → content (title, help, answers) → footer
 *
 * - The stepper (back arrow + 3 phase segments) sits at a fixed spot directly
 *   under the logo on every step — never inside the card — so it does not move
 *   from question to question. The first segment fills with question progress.
 * - Content is normal document flow: answers sit right under the question/help
 *   list. Long pages simply grow and the whole page scrolls (no split panes,
 *   no pinned-mid-screen answer strip).
 * - The CTA never scrolls off-screen. Mobile floats it fixed at the bottom
 *   (same pattern as JourneyLayout / CarePlus); desktop pins it sticky at the
 *   bottom of the content column with the same white fade. Sign in sits at the
 *   bottom (just above the CTA), or under the content when content is long.
 * - `showHero` only toggles the hero region; chrome stays the same pattern.
 * - Pass the primary action via `cta` (not as children) so mobile can float it.
 */

import React, { useCallback, useEffect, useRef, useState, isValidElement, cloneElement} from "react";
import HeaderController from "../../../components/Headers/HeaderController";
import { ShellContext, ShellScopedId, useShellId } from "./shellContext";
import { ScreenTransitionStyles, screenTransitionClass, useScreenTransition } from "./screenTransition";
import * as translator from "../../../utils/translator";

const t = (language: string | undefined, key: string): string =>
  translator.translate(language, "default", "QuestionnairePage/" + key) || "";

interface QuestionFrameProps {
  heroDesktop?: string;
  heroMobile?: string;
  discountBadgeDesktop?: string;
  discountBadgeMobile?: string;
  stepCurrent: number;
  stepTotal: number;
  titleHtml?: string;
  subtitleHtml?: string;
  whyWeAskBlock?: string;
  onWhyWeAskClick?: () => void;
  isFollowUp?: boolean;
  onBack: () => void;
  onSignIn: () => void;
  language?: string;
  showHero?: boolean;
  showSignInLink?: boolean;
  /** Replaces the built-in sign-in block (at the bottom, just above the CTA when there is one). */
  footer?: React.ReactNode;
  /** Small print under the CTA on desktop and right under the content on mobile. */
  footerNote?: React.ReactNode;
  /** Replace the default title / subtitle classes for pages with their own Figma type scale. */
  titleClassName?: string;
  subtitleClassName?: string;
  // Kept for callers (e.g. legal consent). Not used by the current layout:
  // long content scrolls via the capped blocks above and, on mobile, the CTA
  // floats fixed at the bottom regardless.
  fitViewport?: boolean;
  cta?: React.ReactNode;
  /** Pinned to the bottom of the content column, directly below the sign-in link (scrolls with long content). */
  bottomContent?: React.ReactNode;
  /** DOM id for the page root; interstitials and special pages override it. */
  pageId?: string;
  /**
   * DOM id for the question content wrapper: `question_{recipe_questions.id}`,
   * or `question_{parent}_sub_{recipe_answers.id}` on a follow-up. Unset on
   * pages with no recipe question behind them. Desktop renders it as-is and
   * mobile adds `_mobile`.
   */
  questionDomId?: string;
  children: React.ReactNode;
}

/**
 * Capped, internally-scrolling HTML block (help_block / subtitle).
 * Figma: a long condition list slides under the answers with a soft fade
 * instead of a hard cut or a visible scrollbar. The fade is only applied
 * while there is more content below (it disappears at the bottom / when
 * the list fits), so short help texts are untouched.
 */
const ScrollFadeHtml: React.FC<{ className: string; html: string }> = ({ className, html }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setHasMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 2);
  }, []);

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el) return;
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure, html]);

  return (
    <div
      ref={ref}
      onScroll={measure}
      className={`${className} [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}
      style={
        hasMoreBelow
          ? {
              maskImage: "linear-gradient(to bottom, #000 calc(100% - 56px), transparent)",
              WebkitMaskImage: "linear-gradient(to bottom, #000 calc(100% - 56px), transparent)",
            }
          : undefined
      }
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const QuestionFrame: React.FC<QuestionFrameProps> = ({
  heroDesktop,
  heroMobile,
  discountBadgeDesktop,
  discountBadgeMobile,
  stepCurrent,
  stepTotal,
  titleHtml,
  subtitleHtml,
  whyWeAskBlock,
  onWhyWeAskClick,
  isFollowUp = false,
  onBack,
  onSignIn,
  language,
  showHero = true,
  showSignInLink = true,
  footer,
  footerNote,
  titleClassName,
  subtitleClassName,
  fitViewport: _fitViewport = false,
  cta,
  bottomContent,
  pageId = "wl2_question_page",
  questionDomId,
  children,
}) => {
  // Figma's stepper is 3 fixed funnel-phase segments (first one flexible, the
  // other two ~71px): questionnaire, shipping, payment. Only the questionnaire
  // segment (index 0) is wired up here — it fills as the user answers
  // questions. Shipping/payment (indices 1-2) stay empty until those phases
  // are wired up separately.
  // Keyed on the transition so a new screen remounts and plays its slide-in.
  const transition = useScreenTransition();
  const transitionClass = screenTransitionClass(transition.direction);

  const PHASE_COUNT = 3;
  const questionnaireFill = stepTotal > 0 ? Math.min(Math.max(stepCurrent / stepTotal, 0), 1) : 0;
  const phaseFill = (i: number) => (i === 0 ? questionnaireFill : 0);

  const progressBar = (
    <div
      className="flex flex-1 min-w-0 items-center gap-[5px]"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={stepTotal}
      aria-valuenow={stepCurrent}
      aria-label="Questionnaire progress"
    >
      {Array.from({ length: PHASE_COUNT }).map((_, i) => (
        <div
          key={i}
          className={`h-[5px] rounded-full overflow-hidden bg-[rgba(222,227,237,0.73)] ${
            i === 0 ? "flex-1 min-w-px" : "flex-[0_1_71px] min-w-[24px]"
          }`}
        >
          <div
            className="h-full rounded-full"
            style={{
              width: `${phaseFill(i) * 100}%`,
              backgroundImage: "linear-gradient(90deg, #f59402 44.231%, #efcc3f 100%)",
            }}
          />
        </div>
      ))}
    </div>
  );

  // Rendered once at the top of the page (under the logo), above the hero —
  // never inside the card — so it stays in the same place on every question.
  const progressRow = (
    <div className="flex items-center">
      <ShellScopedId base="wl2_back_button">
        {(backId) => (
          <button
            id={backId}
            onClick={onBack}
            aria-label="Back"
            className="w-11 h-11 flex items-center justify-center text-[#5c6672] flex-shrink-0 cursor-pointer hover:text-[#08080a] active:scale-95 transition"
            style={{ border: "none", background: "transparent", padding: 0 }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
      </ShellScopedId>
      <div className="flex flex-1 min-w-0 items-center px-4">{progressBar}</div>
    </div>
  );

  // Readable title — slightly under Figma H4 so long lists still fit better.
  const titleNode = titleHtml ? (
    <div
      className={titleClassName ?? "font-['Poppins'] font-semibold text-[#08080a] text-[24px] lg:text-[28px] leading-snug mb-6"}
      dangerouslySetInnerHTML={{ __html: titleHtml }}
    />
  ) : null;

  // help_block / subtitle: readable body (~Figma 16, slightly tighter gap).
  // Nested selectors normalize BO HTML lists (inline ul/li + icons).
  // Do not force flex-col on every ul — BO may use display:grid for 2-col lists.
  // A long CMS help_block (e.g. a 12-item condition list) must not push the
  // answers off the fold. Cap it and let the block scroll internally so the
  // answer controls stay visible on short viewports.
  const defaultSubtitleClassName =
    "font-['Poppins'] text-[14px] lg:text-[15px] leading-[1.4] text-[#99a3b2] mb-8 " +
    "overflow-y-auto overscroll-contain max-h-[40dvh] md:max-h-[34dvh] " +
    "[&_ul]:!m-0 [&_ul]:!p-0 [&_ul]:!list-none [&_ul]:!gap-2 " +
    "[&_ul:not([style*='grid'])]:!flex [&_ul:not([style*='grid'])]:!flex-col " +
    "[&_li]:!m-0 [&_li]:!flex [&_li]:!items-start [&_li]:!gap-2 " +
    "[&_li]:!text-[15px] [&_li]:!leading-[1.4] [&_li]:!text-[rgba(0,0,0,0.8)] " +
    "[&_li]:!font-['Roboto',Arial,sans-serif] " +
    "[&_li_svg]:!size-4 [&_li_svg]:!min-w-[16px] [&_li_svg]:!mt-0.5 [&_li_svg]:!shrink-0 " +
    "[&_li_img]:!size-4 [&_li_img]:!min-w-[16px] [&_li_img]:!mt-0.5 [&_li_img]:!shrink-0 " +
    "[&_li_span]:!text-[15px] [&_li_span]:!leading-[1.4] " +
    "[&_p]:!m-0 [&_p]:!text-[15px] [&_p]:!leading-[1.4]";

  // Mobile only: the shared questionnaire inputs (text / number / imperial use
  // .numberInput-*, date uses react-datepicker) default to 42px with a 12-14px
  // date font. Match the 60px answer cards and keep 16px so iOS doesn't zoom.
  const mobileInputsClassName =
    "[&_.numberInput-container]:!h-[60px] [&_.numberInput-error-container]:!h-[60px] " +
    "[&_.numberInput-container]:!text-[16px] [&_.numberInput-error-container]:!text-[16px] " +
    // String.raw keeps `\_` (a literal underscore for Tailwind) identical in
    // source and at runtime, so the generated selector matches the class.
    String.raw`[&_.react-datepicker\_\_input-container]:!h-[60px] ` +
    String.raw`[&_.react-datepicker\_\_input-container>input]:!text-[16px] ` +
    String.raw`[&_.react-datepicker\_\_input-container>input::placeholder]:!text-[16px]`;

  // Normal flow: title → help → answers (no mid-page pin).
  const contentBody = (
    <>
      {isFollowUp && (
        <div className="flex justify-start mb-3">
          <span className="inline-block rounded-[20px] bg-[rgba(224,224,224,0.22)] px-2 py-1 font-['Poppins'] text-[12px] text-[#f69502]">
            {t(language, "follow_up_question")}
          </span>
        </div>
      )}
      {titleNode}
      {subtitleHtml && <ScrollFadeHtml className={subtitleClassName ?? defaultSubtitleClassName} html={subtitleHtml} />}
      {whyWeAskBlock && (
        <div className="mt-1 mb-8 flex justify-start lg:justify-center">
          <ShellScopedId base="wl2_why_we_ask_link">
            {(whyId) => (
              <button
                id={whyId}
                onClick={onWhyWeAskClick}
                className="inline-flex items-center gap-[6px] font-['Poppins'] text-[14px] text-[#1f638c] lg:text-[#269C6E] lg:underline cursor-pointer hover:opacity-80 transition"
                style={{ border: "none", background: "transparent", padding: 0 }}
              >
                <svg className="lg:hidden shrink-0" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8.0026 14.6615C11.6845 14.6615 14.6693 11.6767 14.6693 7.99479C14.6693 4.31289 11.6845 1.32812 8.0026 1.32812C4.32071 1.32812 1.33594 4.31289 1.33594 7.99479C1.33594 11.6767 4.32071 14.6615 8.0026 14.6615Z"
                    stroke="#257DB0"
                    strokeWidth="1.33333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6.0625 6.00038C6.21924 5.55483 6.5286 5.17912 6.9358 4.9398C7.343 4.70049 7.82176 4.61301 8.28728 4.69285C8.7528 4.7727 9.17504 5.01473 9.47922 5.37607C9.78339 5.7374 9.94987 6.19473 9.94917 6.66705C9.94917 8.00038 7.94917 8.66705 7.94917 8.66705"
                    stroke="#257DB0"
                    strokeWidth="1.33333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path d="M8 11.3281H8.00667" stroke="#257DB0" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {t(language, "why_we_ask_link")}
              </button>
            )}
          </ShellScopedId>
        </div>
      )}
      {children}
    </>
  );

  // Pushed to the bottom (just above the CTA) when there is room; long content keeps it under the question.
  const signInBlock =
    footer ??
    (showSignInLink && (
      <div className="text-center">
        <p className="font-['Poppins'] text-[14px] lg:text-[16px] text-[#99a3b2] m-0">{t(language, "signin_prompt")}</p>
        <ShellScopedId base="wl2_signin_link">
          {(signInId) => (
            <button
              id={signInId}
              onClick={onSignIn}
              className="font-['Poppins'] text-[14px] lg:text-[18px] text-[#269C6E] underline mt-1 cursor-pointer hover:opacity-80 transition"
              style={{ border: "none", background: "transparent" }}
            >
              {t(language, "signin_link")}
            </button>
          )}
        </ShellScopedId>
      </div>
    ));

  // Sticky so long content scrolls under the answers and they keep the same spot on every question.
  const bottomBlock = (stickyClassName: string, style?: React.CSSProperties) =>
    bottomContent ? (
      <div
        className={`w-full mt-auto sticky z-30 ${signInBlock ? "pt-4" : "pt-8"} ${stickyClassName}`}
        style={{ backgroundImage: "linear-gradient(to bottom, rgba(255,255,255,0), #fff 16px)", ...style }}
      >
        {signInBlock}
        <div className={signInBlock ? "pt-4" : ""}>{bottomContent}</div>
      </div>
    ) : (
      signInBlock && <div className="w-full pt-4 mt-auto">{signInBlock}</div>
    );

  return (
    // overflow-x-clip: the horizontal slide-in must not add a sideways scroll.
    <div id={useShellId(pageId)} className="min-h-screen min-h-dvh bg-white overflow-x-clip">
      <ScreenTransitionStyles />
      {/* Desktop shell from md (768px)+ */}
      {/* Provider renders no DOM: ids inside keep their plain `{base}`. */}
      <ShellContext.Provider value="desktop">
      <div className="hidden md:flex md:flex-col md:min-h-screen">
        {/* showBackButton=false: Variant B owns back via the stepper arrow. */}
        <HeaderController showSteps={false} showBackButton={false} stage="Questionnaire" isCannabis={false} />

        {/* Stepper: under the logo on every step. */}
        <div className="w-full max-w-[760px] mx-auto px-6 shrink-0 pt-[11px]">
          <div className="w-full max-w-[698px] mx-auto px-8">{progressRow}</div>
        </div>

        {/* Content stretches; long questions push footer down (page scrolls). */}
        <div
          key={transition.key}
          className={`flex-1 w-full max-w-[760px] mx-auto px-6 flex flex-col items-center pt-3 pb-8 ${transitionClass}`}
        >
          {showHero && (
            <div className="relative w-full shrink-0 rounded-[24px] overflow-hidden max-h-[min(220px,26vh)] aspect-[16/7] mb-5">
              <img
                src={heroDesktop}
                alt=""
                className="absolute inset-0 w-full h-full object-cover object-top"
                loading="eager"
              />
              {discountBadgeDesktop && (
                <img
                  src={discountBadgeDesktop}
                  alt=""
                  className="absolute h-auto"
                  style={{ left: "67.9%", top: "30.9%", width: "19.7%" }}
                />
              )}
            </div>
          )}

          <div className="w-full max-w-[698px] px-8 pt-2 flex-1 flex flex-col">
            <ShellScopedId base={questionDomId}>
              {(wrapperId) => <div id={wrapperId} className="w-full">{contentBody}</div>}
            </ShellScopedId>
            {bottomBlock("bottom-8 shadow-[0_32px_0_#fff]")}
            {/* Sticky CTA: a direct child of this full-height column (sticky is
                bounded by its parent), so it pins to the viewport bottom over
                long content. Sticky rather than fixed because the transition
                wrapper above is transformed while it animates. */}
            {cta && (
              <ShellScopedId base="wl2_cta_slot">
                {(slotId) => (
                  <div
                    id={slotId}
                    className={`w-full ${signInBlock ? "" : "mt-auto"} sticky bottom-0 z-40 pt-8 pb-5 [&_button]:mt-0`}
                    style={{ backgroundImage: "linear-gradient(to bottom, rgba(255,255,255,0), #fff 30%)" }}
                  >
                    {cta}
                  </div>
                )}
              </ShellScopedId>
            )}
            {footerNote && (
              <div className={`w-full ${cta ? "" : "pt-8"} ${cta || signInBlock ? "" : "mt-auto"}`}>{footerNote}</div>
            )}
          </div>
        </div>
      </div>
      </ShellContext.Provider>

      {/* Mobile shell: only below md */}
      {/* Same sub-trees as above; ids inside resolve to `{base}_mobile`. */}
      <ShellContext.Provider value="mobile">
      <div className={`md:hidden flex flex-col min-h-screen min-h-dvh ${mobileInputsClassName}`}>
        <div className="shrink-0 w-full px-3 pt-2 pb-3">{progressRow}</div>

        {/* Slides on screen change; the fixed CTA below stays outside it (a
            transformed ancestor would re-anchor position:fixed). */}
        <div key={transition.key} className={`flex-1 flex flex-col w-full ${transitionClass}`}>
        {showHero && (
          <div className="relative w-full shrink-0 overflow-hidden aspect-[4/3] max-h-[min(240px,32dvh)]">
            <img
              src={heroMobile}
              alt=""
              className="absolute inset-0 w-full h-full object-cover object-top"
              loading="eager"
            />
            {discountBadgeMobile && (
              <img
                src={discountBadgeMobile}
                alt=""
                className="absolute h-auto"
                style={{
                  left: "66.2%",
                  top: "42.8%",
                  width: "28.2%",
                  background: "rgba(89, 89, 89, 0.30)",
                  borderRadius: "14.93px",
                }}
              />
            )}
          </div>
        )}

        {/* pb-36 clears the fixed CTA; safe-area padding clears the home indicator. */}
        <div
          className={`bg-white relative w-full px-5 pt-6 flex-1 flex flex-col ${
            showHero ? "rounded-t-[24px]" : ""
          } ${cta ? "pb-36" : ""}`}
          style={cta ? undefined : { paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}
        >
          <ShellScopedId base={questionDomId}>
            {(wrapperId) => <div id={wrapperId} className="w-full">{contentBody}</div>}
          </ShellScopedId>
          {footerNote && <div className="w-full pt-4">{footerNote}</div>}
          {bottomBlock("shadow-[0_64px_0_#fff]", { bottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" })}
        </div>
        </div>

        {/* Floating mobile CTA — always visible, matches JourneyLayout / CarePlus. */}
        {cta && (
          <div
            className="fixed bottom-0 left-0 right-0 z-40 px-5 pt-8"
            style={{
              backgroundImage: "linear-gradient(to bottom, rgba(255,255,255,0), #fff 30%)",
              // Keep the CTA clear of the iOS/Android home indicator.
              paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 20px)",
            }}
          >
            {/* Drop in-flow top margins (mt-6 etc.) that only apply on desktop. */}
            <div className="[&_button]:!mt-0">
              {isValidElement(cta) ? cloneElement(cta) : cta}
            </div>
          </div>
        )}
      </div>
      </ShellContext.Provider>
    </div>
  );
};

export default QuestionFrame;
